import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Save, Send, ChevronDown, ChevronUp, Edit3, FolderPlus } from 'lucide-react';

const emptyDuty = () => ({
  id: Math.random().toString(36).substr(2, 9), date: '', topics: '', presea_classes_hrs: 0,
  presea_practicals_hrs: 0, online_classes_hrs: 0, post_sea_classes_hrs: 0, total_hours: 0,
  days_absent: 0, remarks: '', status: 'InProgress'
});

const emptyResponsibility = (groupName = 'General') => ({
  id: Math.random().toString(36).substr(2, 9), group_name: groupName, date: '', topics: '', description: '',
  time_spent_hrs: 0, planned_hrs: 0, deadline: '', closing_out_date: '',
  ssv_princi_comments: '', cic_comments: '', status: 'InProgress'
});

const emptyMarketing = () => ({
  id: Math.random().toString(36).substr(2, 9), date: '', topics: '', description: '',
  deadline: '', ssv_comments: '', csa_comments: '', status: 'InProgress'
});

// A row is "blank" if the user never typed anything meaningful into it — filtered out
// before saving so empty rows never pollute reports/analytics.
function isBlankDuty(d) {
  return !d.topics?.trim() && !d.remarks?.trim() && !(parseFloat(d.total_hours) || 0) && !(parseInt(d.days_absent) || 0);
}
function isBlankResponsibility(r) {
  return !r.topics?.trim() && !r.description?.trim() && !(parseFloat(r.time_spent_hrs) || 0) && !(parseFloat(r.planned_hrs) || 0);
}
function isBlankMarketing(m) {
  return !m.topics?.trim() && !m.description?.trim() && !(parseFloat(m.hours) || 0);
}

export default function ReportEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPrincipal = user?.role === 'PrincipalSSV';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [weekFrom, setWeekFrom] = useState('');
  const [weekTo, setWeekTo] = useState('');
  const [duties, setDuties] = useState([]);
  const [responsibilities, setResponsibilities] = useState([]);
  const [marketings, setMarketings] = useState([]);
  const [expandedSections, setExpandedSections] = useState({ academics: true, responsibilities: true, marketing: false });
  const [editingGroupName, setEditingGroupName] = useState(null);
  const [tempGroupName, setTempGroupName] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.getReport(id);
        if (data.faculty_id !== user?._id || !['Draft', 'Rejected'].includes(data.report_status)) {
          navigate('/reports');
          return;
        }
        setWeekFrom(data.week_from);
        setWeekTo(data.week_to);
        setDuties(data.regular_duties?.length > 0 ? data.regular_duties : [emptyDuty()]);
        // Ensure group_name exists on old data
        const resps = (data.responsibilities?.length > 0 ? data.responsibilities : [emptyResponsibility()]).map(r => ({
          ...r, group_name: r.group_name || 'General'
        }));
        setResponsibilities(resps);
        setMarketings(data.course_marketings?.length > 0 ? data.course_marketings : [emptyMarketing()]);
      } catch (err) {
        setError('Failed to load report');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, user, navigate]);

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));

  const updateDuty = (index, field, value) => {
    setDuties(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if (['presea_classes_hrs', 'presea_practicals_hrs', 'online_classes_hrs', 'post_sea_classes_hrs'].includes(field)) {
        const d = next[index];
        d.total_hours = (parseFloat(d.presea_classes_hrs) || 0) + (parseFloat(d.presea_practicals_hrs) || 0) +
          (parseFloat(d.online_classes_hrs) || 0) + (parseFloat(d.post_sea_classes_hrs) || 0);
      }
      return next;
    });
  };

  // Responsibility groups
  const getGroups = () => {
    const groups = []; const seen = new Set();
    responsibilities.forEach(r => { if (!seen.has(r.group_name)) { seen.add(r.group_name); groups.push(r.group_name); } });
    return groups;
  };
  const getGroupRows = (g) => responsibilities.filter(r => r.group_name === g);
  const addGroup = () => {
    const names = getGroups(); let n = 'New Responsibility'; let c = 1;
    while (names.includes(n)) n = `New Responsibility ${c++}`;
    setResponsibilities(prev => [...prev, emptyResponsibility(n)]);
  };
  const renameGroup = (old, nw) => {
    if (!nw.trim() || (nw.trim() !== old && getGroups().includes(nw.trim()))) return;
    setResponsibilities(prev => prev.map(r => r.group_name === old ? { ...r, group_name: nw.trim() } : r));
    setEditingGroupName(null);
  };
  const deleteGroup = (g) => { if (getGroups().length <= 1) return; setResponsibilities(prev => prev.filter(r => r.group_name !== g)); };
  const addRowToGroup = (g) => setResponsibilities(prev => [...prev, emptyResponsibility(g)]);
  const updateResp = (id, f, v) => setResponsibilities(prev => prev.map(r => r.id === id ? { ...r, [f]: v } : r));
  const removeResp = (id, g) => { setResponsibilities(prev => prev.filter(r => r.id !== id)); };

  const handleSave = async (submit = false) => {
    setError(''); setSaving(true);
    try {
      const payload = {
        regular_duties: duties.filter(d => !isBlankDuty(d)).map(d => ({ ...d, presea_classes_hrs: parseFloat(d.presea_classes_hrs) || 0, presea_practicals_hrs: parseFloat(d.presea_practicals_hrs) || 0, online_classes_hrs: parseFloat(d.online_classes_hrs) || 0, post_sea_classes_hrs: parseFloat(d.post_sea_classes_hrs) || 0, days_absent: parseInt(d.days_absent) || 0 })),
        responsibilities: responsibilities.filter(r => !isBlankResponsibility(r)).map(r => ({ ...r, time_spent_hrs: parseFloat(r.time_spent_hrs) || 0, planned_hrs: parseFloat(r.planned_hrs) || 0 })),
        course_marketings: marketings.filter(m => !isBlankMarketing(m)),
      };
      await api.updateReport(id, payload);
      if (submit) await api.submitReport(id);
      navigate(`/reports/${id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save report');
    } finally { setSaving(false); }
  };

  const statusOptions = ['InProgress', 'Completed', 'Overdue'];
  const statusColors = { InProgress: 'bg-amber-100 text-amber-800', Completed: 'bg-emerald-100 text-emerald-800', Overdue: 'bg-red-100 text-red-800' };
  const groups = getGroups();

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400 text-sm">Loading...</div>;

  return (
    <div data-testid="report-edit-page" className="space-y-6 animate-fade-in max-w-[1400px]">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Edit Report</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {weekFrom} — {weekTo}
        </h1>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

      {/* Section 1 */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-0 cursor-pointer" onClick={() => toggleSection('academics')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold">1</div>
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">Academics / Regular Duties</CardTitle>
            </div>
            {expandedSections.academics ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections.academics && (
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-slate-200 text-sm">
                <thead><tr className="bg-slate-50">
                  {['Date', 'Topics', 'Presea Cls', 'Presea Prac', 'Online Cls', 'Post Sea', 'Total', 'Absent', 'Remarks', 'Status', ''].map(h => (
                    <th key={h} className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-200 whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {duties.map((d, i) => (
                    <tr key={d.id} className="border-b border-slate-200">
                      <td className="border border-slate-200 p-1"><Input type="date" value={d.date} onChange={e => updateDuty(i, 'date', e.target.value)} className="h-8 text-xs border-0 shadow-none" /></td>
                      <td className="border border-slate-200 p-1"><Input value={d.topics} onChange={e => updateDuty(i, 'topics', e.target.value)} className="h-8 text-xs border-0 shadow-none min-w-[120px]" /></td>
                      <td className="border border-slate-200 p-1"><Input type="number" step="0.5" min="0" value={d.presea_classes_hrs} onChange={e => updateDuty(i, 'presea_classes_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                      <td className="border border-slate-200 p-1"><Input type="number" step="0.5" min="0" value={d.presea_practicals_hrs} onChange={e => updateDuty(i, 'presea_practicals_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                      <td className="border border-slate-200 p-1"><Input type="number" step="0.5" min="0" value={d.online_classes_hrs} onChange={e => updateDuty(i, 'online_classes_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                      <td className="border border-slate-200 p-1"><Input type="number" step="0.5" min="0" value={d.post_sea_classes_hrs} onChange={e => updateDuty(i, 'post_sea_classes_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                      <td className="border border-slate-200 p-1 text-center font-bold text-slate-700">{d.total_hours}</td>
                      <td className="border border-slate-200 p-1"><Input type="number" value={d.days_absent} onChange={e => updateDuty(i, 'days_absent', e.target.value)} className="h-8 text-xs border-0 shadow-none w-14" /></td>
                      <td className="border border-slate-200 p-1"><Input value={d.remarks} onChange={e => updateDuty(i, 'remarks', e.target.value)} className="h-8 text-xs border-0 shadow-none min-w-[100px]" /></td>
                      <td className="border border-slate-200 p-1">
                        <select value={d.status} onChange={e => updateDuty(i, 'status', e.target.value)} className={`h-8 text-xs rounded px-1 ${statusColors[d.status]}`}>
                          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="border border-slate-200 p-1"><button onClick={() => setDuties(p => p.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={() => setDuties(p => [...p, emptyDuty()])} className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 bg-transparent border border-dashed border-slate-300 hover:border-slate-400 rounded-md px-3 py-2 w-full justify-center">
              <Plus className="w-3.5 h-3.5" /> Add Row
            </button>
          </CardContent>
        )}
      </Card>

      {/* Section 2: Grouped Responsibilities */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-0 cursor-pointer" onClick={() => toggleSection('responsibilities')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-slate-900 flex items-center justify-center text-white text-xs font-bold">2</div>
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">Responsibilities</CardTitle>
              <span className="text-xs text-slate-400 ml-2">{groups.length} group(s)</span>
            </div>
            {expandedSections.responsibilities ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections.responsibilities && (
          <CardContent className="pt-4 space-y-5">
            {groups.map((groupName) => {
              const rows = getGroupRows(groupName);
              const isEditing = editingGroupName === groupName;
              return (
                <div key={groupName} className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between border-b border-slate-200">
                    <div className="flex items-center gap-2 flex-1">
                      <div className="w-2 h-2 rounded-full bg-slate-400" />
                      {isEditing ? (
                        <input type="text" value={tempGroupName} onChange={e => setTempGroupName(e.target.value)}
                          onBlur={() => renameGroup(groupName, tempGroupName)}
                          onKeyDown={e => { if (e.key === 'Enter') renameGroup(groupName, tempGroupName); if (e.key === 'Escape') setEditingGroupName(null); }}
                          autoFocus className="text-sm font-bold text-slate-800 bg-white border border-slate-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-slate-900 w-64" />
                      ) : (
                        <span className="text-sm font-bold text-slate-800">{groupName}</span>
                      )}
                      {!isEditing && (
                        <button onClick={e => { e.stopPropagation(); setEditingGroupName(groupName); setTempGroupName(groupName); }}
                          className="text-slate-400 hover:text-slate-600 p-0.5" title="Rename">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <span className="text-xs text-slate-400">({rows.length})</span>
                    </div>
                    {isPrincipal && groups.length > 1 && <button onClick={() => deleteGroup(groupName)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead><tr className="bg-slate-50/50">
                        {['Date', 'Topics', 'Description', 'Time Spent', 'Planned', 'Deadline', 'Closing Date', 'Status', ''].map(h => (
                          <th key={h} className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 whitespace-nowrap">{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {rows.map(r => (
                          <tr key={r.id} className="border-b border-slate-100">
                            <td className="border-r border-slate-100 p-1"><Input type="date" value={r.date} onChange={e => updateResp(r.id, 'date', e.target.value)} className="h-8 text-xs border-0 shadow-none" /></td>
                            <td className="border-r border-slate-100 p-1"><Input value={r.topics} onChange={e => updateResp(r.id, 'topics', e.target.value)} className="h-8 text-xs border-0 shadow-none min-w-[120px]" /></td>
                            <td className="border-r border-slate-100 p-1"><Input value={r.description} onChange={e => updateResp(r.id, 'description', e.target.value)} className="h-8 text-xs border-0 shadow-none min-w-[150px]" /></td>
                            <td className="border-r border-slate-100 p-1"><Input type="number" step="0.5" min="0" value={r.time_spent_hrs} onChange={e => updateResp(r.id, 'time_spent_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                            <td className="border-r border-slate-100 p-1"><Input type="number" step="0.5" min="0" value={r.planned_hrs} onChange={e => updateResp(r.id, 'planned_hrs', e.target.value)} className="h-8 text-xs border-0 shadow-none w-16" /></td>
                            <td className="border-r border-slate-100 p-1"><Input type="date" value={r.deadline} onChange={e => updateResp(r.id, 'deadline', e.target.value)} className="h-8 text-xs border-0 shadow-none" /></td>
                            <td className="border-r border-slate-100 p-1"><Input type="date" value={r.closing_out_date} onChange={e => updateResp(r.id, 'closing_out_date', e.target.value)} className="h-8 text-xs border-0 shadow-none" /></td>
                            <td className="border-r border-slate-100 p-1">
                              <select value={r.status} onChange={e => updateResp(r.id, 'status', e.target.value)} className={`h-8 text-xs rounded px-1 ${statusColors[r.status]}`}>
                                {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </td>
                            <td className="p-1"><button onClick={() => removeResp(r.id, groupName)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button onClick={() => addRowToGroup(groupName)} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 px-4 py-2.5 w-full border-t border-dashed border-slate-200 hover:bg-slate-50">
                    <Plus className="w-3.5 h-3.5" /> Add Entry
                  </button>
                </div>
              );
            })}
            <button onClick={addGroup} className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-dashed border-blue-300 hover:border-blue-400 rounded-lg px-4 py-3 w-full justify-center">
              <FolderPlus className="w-4 h-4" /> Add New Responsibility
            </button>
          </CardContent>
        )}
      </Card>

      {/* Section 3 */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-0 cursor-pointer" onClick={() => toggleSection('marketing')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-emerald-600 flex items-center justify-center text-white text-xs font-bold">3</div>
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">Initiatives</CardTitle>
            </div>
            {expandedSections.marketing ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </CardHeader>
        {expandedSections.marketing && (
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-slate-200 text-sm">
                <thead><tr className="bg-slate-50">
                  {['Date', 'Topics', 'Description', 'Deadline', 'Status', ''].map(h => (
                    <th key={h} className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-200 whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {marketings.map((m, i) => (
                    <tr key={m.id} className="border-b border-slate-200">
                      <td className="border border-slate-200 p-1"><Input type="date" value={m.date} onChange={e => setMarketings(p => { const n = [...p]; n[i] = { ...n[i], date: e.target.value }; return n; })} className="h-8 text-xs border-0 shadow-none" /></td>
                      <td className="border border-slate-200 p-1"><Input value={m.topics} onChange={e => setMarketings(p => { const n = [...p]; n[i] = { ...n[i], topics: e.target.value }; return n; })} className="h-8 text-xs border-0 shadow-none min-w-[120px]" /></td>
                      <td className="border border-slate-200 p-1"><Input value={m.description} onChange={e => setMarketings(p => { const n = [...p]; n[i] = { ...n[i], description: e.target.value }; return n; })} className="h-8 text-xs border-0 shadow-none min-w-[150px]" /></td>
                      <td className="border border-slate-200 p-1"><Input type="date" value={m.deadline} onChange={e => setMarketings(p => { const n = [...p]; n[i] = { ...n[i], deadline: e.target.value }; return n; })} className="h-8 text-xs border-0 shadow-none" /></td>
                      <td className="border border-slate-200 p-1">
                        <select value={m.status} onChange={e => setMarketings(p => { const n = [...p]; n[i] = { ...n[i], status: e.target.value }; return n; })} className={`h-8 text-xs rounded px-1 ${statusColors[m.status]}`}>
                          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="border border-slate-200 p-1"><button onClick={() => setMarketings(p => p.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={() => setMarketings(p => [...p, emptyMarketing()])} className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 bg-transparent border border-dashed border-slate-300 hover:border-slate-400 rounded-md px-3 py-2 w-full justify-center">
              <Plus className="w-3.5 h-3.5" /> Add Row
            </button>
          </CardContent>
        )}
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-3 justify-end pb-8">
        <Button variant="ghost" onClick={() => navigate(`/reports/${id}`)} className="text-slate-600 hover:bg-slate-100">Cancel</Button>
        <Button data-testid="save-edit-btn" onClick={() => handleSave(false)} disabled={saving} className="bg-slate-200 text-slate-800 hover:bg-slate-300">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button data-testid="save-submit-edit-btn" onClick={() => handleSave(true)} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700">
          <Send className="w-4 h-4 mr-1.5" /> {saving ? 'Submitting...' : 'Save & Submit'}
        </Button>
      </div>
    </div>
  );
}
